/* This example requires Tailwind CSS v2.0+ */

import AvatarPlaceholder from "assets/images/avatar.png";

import { APP_ROUTES } from "const";
import { Link, useLocation } from "react-router-dom";
import classnames from "classnames";
import { ReactComponent as Help } from "assets/images/help.svg";
import { ReactComponent as NotificationIcon } from "assets/images/notification.svg";
import { ReactComponent as Facebook } from "assets/images/facebook.svg";
import { ReactComponent as Instagram } from "assets/images/instagram.svg";
import { ReactComponent as Twitter } from "assets/images/twitter.svg";
import { ChevronDownIcon } from "@heroicons/react/outline";
import { Button, ProductTourComponent } from "components";
import { IUser } from "types";
import { logout } from "utils";
import { SlideOver } from "components/SlideOver";
import { Step } from "react-joyride";
import { fullProductTour } from "const/tourSteps";
import { useFindUnreadActivitiesAmount, useWindowDimensions } from "hooks";
import { memo, useEffect, useMemo, useState } from "react";
import { isNumber } from "lodash";

// Editorial Logo Component for mobile menu - matches Documentary Editorial style
function EditorialLogoMobile() {
  return (
    <div className="inline-flex items-baseline">
      <span
        className="text-2xl font-semibold"
        style={{
          color: '#1C1917',
          fontFamily: "'Fraunces', serif"
        }}
      >
        WE
      </span>
      <span
        className="text-2xl font-light italic"
        style={{
          color: '#44403C',
          fontFamily: "'Fraunces', serif"
        }}
      >
        attitude
      </span>
      <span className="text-xs font-medium opacity-50">.org</span>
    </div>
  );
}

export interface State {
  run: boolean;
  stepIndex: number;
  steps: Step[];
}

interface INavLink {
  name: string;
  href: string;
  group: "primary" | "secondary";
  stepClasses?: string;
}

const NAV_LINKS: INavLink[] = [
  { name: "Issues", href: APP_ROUTES.ISSUES, group: "primary" },
  {
    name: "Solutions",
    href: APP_ROUTES.SOLUTION_LIST,
    group: "primary",
    stepClasses:
      "one-first-step two-first-step three-first-step five-first-step six-first-step ten-first-step eleven-first-step",
  },
  {
    name: "Action Groups",
    href: APP_ROUTES.ACTION_GROUP_LIST,
    group: "primary",
    stepClasses: "four-first-step seven-first-step",
  },
  {
    name: "Dashboard",
    href: APP_ROUTES.DASHBOARD,
    group: "secondary",
    stepClasses:
      "thirteen-first-step fourteen-first-step fifteen-first-step sixteen-first-step",
  },
  {
    name: "Impact",
    href: APP_ROUTES.IMPACT,
    group: "secondary",
  },
  {
    name: "Motivational Contents",
    href: APP_ROUTES.MOTIVATIONAL_LIST,
    group: "secondary",
    stepClasses: "twelve-first-step",
  },
  {
    name: "Handout Sheets",
    href: APP_ROUTES.HANDOUTS,
    group: "secondary",
  },
];

type NavItemWithState = INavLink & { current: boolean };

function SlideMenuList({
  isOpen,
  preventClose,
  setIsOpen,
  isScrolled: _isScrolled,
  isAuth,
  profile,
  notEnoughPersonalData,
  setOpenNotificationSlideover,
  setOpenProductTourPopup,
  openProductTourPopup,
  setCurrentScenario,
  setScenarioSteps,
  handleClickStart,
  setShowGoSignInModal,
}: {
  isOpen: boolean;
  preventClose: boolean;
  setIsOpen: (value: React.SetStateAction<boolean>) => void;
  isScrolled: boolean;
  isAuth: boolean;
  profile: IUser | undefined;
  notEnoughPersonalData?: boolean;
  setOpenNotificationSlideover?: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenProductTourPopup: (value: React.SetStateAction<boolean>) => void;
  openProductTourPopup: boolean;
  setCurrentScenario: React.Dispatch<React.SetStateAction<number>>;
  setScenarioSteps: React.Dispatch<React.SetStateAction<Step[]>>;
  handleClickStart: (event: React.MouseEvent<HTMLElement>) => void;
  setShowGoSignInModal: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const location = useLocation();
  const dimensions = useWindowDimensions();
  const { data: unreadActivitiesAmount } = useFindUnreadActivitiesAmount({
    enabled: !!isAuth,
  });
  const amount = +(unreadActivitiesAmount?.data || 0);
  const notificationsAreMoreThanZero =
    isNumber(amount) && !isNaN(amount || 0) && amount > 0;

  const navItems = useMemo<NavItemWithState[]>(() => {
    return NAV_LINKS.map((item) => ({
      ...item,
      current: location.pathname.includes(item.href),
    }));
  }, [location.pathname]);

  const primaryNav = useMemo(
    () => navItems.filter((item) => item.group === "primary"),
    [navItems]
  );

  const secondaryNav = useMemo(
    () => navItems.filter((item) => item.group === "secondary"),
    [navItems]
  );

  const hasActiveInSecondary = useMemo(
    () => secondaryNav.some((item) => item.current),
    [secondaryNav]
  );

  const [moreOpen, setMoreOpen] = useState(hasActiveInSecondary);

  useEffect(() => {
    if (hasActiveInSecondary) {
      setMoreOpen(true);
    }
  }, [hasActiveInSecondary]);

  const closeMenu = () => {
    setTimeout(() => {
      setIsOpen(() => false);
    }, 100);
  };

  const renderNavLink = (
    item: NavItemWithState,
    variant: "primary" | "secondary" = "primary"
  ) => {
    const isSecondary = variant === "secondary";
    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={closeMenu}
        className={classnames(
          "relative flex items-center px-6 py-3 text-left font-medium transition-colors",
          isSecondary ? "text-base" : "text-lg",
          item.stepClasses
        )}
        style={{
          fontFamily: "'Source Sans 3', sans-serif",
          color: item.current ? '#C2410C' : '#44403C',
          fontWeight: item.current ? 600 : 500,
        }}
        aria-current={item.current ? "page" : undefined}
      >
        {/* Left accent bar for active state - terracotta */}
        {item.current && (
          <span
            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 rounded-r-full"
            style={{ background: '#C2410C' }}
          />
        )}
        <span>{item.name}</span>
      </Link>
    );
  };
  return (
    <SlideOver
      open={isOpen}
      preventClose={preventClose}
      position="left"
      close={() => {
        setIsOpen(() => false);
      }}
      classNames={{ headerIcon: "sm:!px-6" }}
      headerIcon={<EditorialLogoMobile />}
    >
      <div className="flex flex-col justify-between h-full">
        <div className="flex flex-col">
          {primaryNav.map((item) => renderNavLink(item))}

          {secondaryNav.length > 0 && (
            <div className="mt-2 border-t" style={{ borderColor: '#E7E5E4' }}>
              <button
                type="button"
                onClick={() => setMoreOpen((prev) => !prev)}
                className="flex w-full items-center justify-between px-6 py-3 text-sm font-semibold tracking-wide transition-colors"
                style={{
                  fontFamily: "'Source Sans 3', sans-serif",
                  color: '#78716C',
                }}
                aria-expanded={moreOpen}
              >
                <span>More</span>
                <ChevronDownIcon
                  className={classnames(
                    "h-4 w-4 transition-transform",
                    { "rotate-180": moreOpen }
                  )}
                />
              </button>
              {moreOpen ? (
                <div className="space-y-1 py-2" style={{ background: '#FAF7F2' }}>
                  {secondaryNav.map((item) => renderNavLink(item, "secondary"))}
                </div>
              ) : (
                <p
                  className="px-6 pb-3 text-xs"
                  style={{
                    fontFamily: "'Source Sans 3', sans-serif",
                    color: '#A8A29E',
                  }}
                >
                  Access dashboards, content tools, and resource sheets without
                  cluttering the main discovery links.
                </p>
              )}
            </div>
          )}
          <a
            href={"https://www.weattitude.org/"}
            target="_blank"
            rel="noreferrer"
            key={"https://www.weattitude.org/"}
            className="items-center px-6 py-3 text-lg flex font-semibold transition-colors"
            style={{
              fontFamily: "'Source Sans 3', sans-serif",
              color: '#C2410C',
            }}
          >
            Homepage →
          </a>
          {isAuth && (
            <div className="pt-4 pb-3 sm:hidden border-t" style={{ borderColor: '#E7E5E4' }}>
              <div className="flex items-center px-6 gap-3">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <Link
                      onClick={() => {
                        setIsOpen(() => false);
                      }}
                      to={APP_ROUTES.PROFILE}
                    >
                      <div
                        className="h-[56px] w-[56px] rounded-full flex items-center justify-center shrink-0 cursor-pointer p-[2px] relative overflow-hidden"
                        style={{
                          border: '2px solid #E7E5E4',
                        }}
                        tabIndex={1}
                      >
                        {!profile?.avatar && (
                          <div className="bg-stone-100 w-full h-full shrink-0 rounded-full flex items-center justify-center">
                            <img src={AvatarPlaceholder} alt="avatar" className="w-8 h-8 opacity-60" />
                          </div>
                        )}
                        {profile?.avatar && (
                          <img
                            className="rounded-full w-full h-full object-cover"
                            src={profile?.avatar}
                            alt=""
                          />
                        )}
                      </div>
                    </Link>
                  </div>
                </div>
                <Link
                  onClick={() => {
                    setIsOpen(() => false);
                  }}
                  to={APP_ROUTES.PROFILE}
                >
                  {
                    <span className=" text-sm font-semibold flex gap-1">
                      {!!profile?.firstName && (
                        <span>{profile?.firstName}</span>
                      )}
                      {!!profile?.lastName && <span>{profile?.lastName}</span>}
                    </span>
                  }
                  {!profile?.firstName && !profile?.lastName && (
                    <span className=" text-sm font-semibold">
                      {profile?.email}
                    </span>
                  )}
                </Link>

                <button
                  type="button"
                  className={classnames(
                    "relative p-1 ml-auto bg-white flex-shrink-0 text-gray-400 hover:text-gray-500 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 ",
                    {
                      " !ring-4 !ring-offset-4 !ring-red-500":
                        notEnoughPersonalData,
                    }
                  )}
                >
                  <span className="sr-only">View notifications</span>
                  {notificationsAreMoreThanZero && (
                    <span
                      className={classnames(
                        " absolute flex  bg-black rounded-full bottom-[60%] left-[60%] text-white outline outline-2 outline-white text-xs font-black  text-white-stroke-sm text-center items-center justify-center",
                        {
                          "w-[22px] h-[22px]": amount > 99,
                        },
                        {
                          "w-[18px] h-[18px]": amount > 9,
                        },
                        {
                          "w-[14px] h-[14px]": amount < 10,
                        }
                      )}
                    >
                      {amount < 100 ? amount : "99+"}
                    </span>
                  )}
                  <NotificationIcon
                    onClick={() => {
                      setOpenNotificationSlideover &&
                        setOpenNotificationSlideover(true);
                    }}
                    className="h-6 w-6"
                    aria-hidden="true"
                  />
                </button>
                {dimensions && dimensions.width < 641 && (
                  <div className="relative flex items-center">
                    <button
                      type="button"
                      onClick={() => {
                        setOpenProductTourPopup((val) => !val);
                      }}
                      className={classnames("relative p-1 rounded-full  ")}
                    >
                      <Help className="h-6 w-6" aria-hidden="true" />
                    </button>
                    {openProductTourPopup && (
                      <ProductTourComponent
                        setIsOpen={setIsOpen}
                        setOpenNotificationSlideover={
                          setOpenNotificationSlideover
                        }
                        mobileVersion={true}
                        setOpenProductTourPopup={setOpenProductTourPopup}
                        setCurrentScenario={setCurrentScenario}
                        setScenarioSteps={setScenarioSteps}
                        handleClickStart={handleClickStart}
                        fullProductTour={fullProductTour}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        {isAuth && (
          <div className="flex flex-col flex-1 px-4 items-center justify-center sm:hidden">
            <Button
              onClick={() => {
                setIsOpen((val) => !val);
                void logout();
              }}
              className="w-full justify-center"
            >
              Sign out
            </Button>
          </div>
        )}
        {!isAuth && (
          <div className="flex px-4 flex-col flex-1 justify-center gap-6 sm:hidden">
            <Link
              onClick={() => {
                setIsOpen(() => false);
              }}
              to={APP_ROUTES.SIGN_UP}
            >
              <Button className="w-full justify-center">Sign up</Button>
            </Link>
            <div className="flex gap-2 text-sm justify-center">
              <span className=" font-light">Existing customer?</span>
              <Link
                onClick={() => {
                  setIsOpen(() => false);
                }}
                className=" font-semibold"
                state={{ from: location?.pathname }}
                to={APP_ROUTES.SIGN_IN}
              >
                Sign in
              </Link>
            </div>
            <div className="flex  items-center justify-end self-end">
              {!!dimensions && dimensions?.width < 641 && (
                <div className="relative flex items-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowGoSignInModal((val) => !val);
                    }}
                    className={classnames("relative p-1 rounded-full  ")}
                  >
                    <Help className="h-6 w-6" aria-hidden="true" />
                  </button>
                  {openProductTourPopup && (
                    <ProductTourComponent
                      setIsOpen={setIsOpen}
                      setOpenNotificationSlideover={
                        setOpenNotificationSlideover
                      }
                      mobileVersion={true}
                      setOpenProductTourPopup={setOpenProductTourPopup}
                      setCurrentScenario={setCurrentScenario}
                      setScenarioSteps={setScenarioSteps}
                      handleClickStart={handleClickStart}
                      fullProductTour={fullProductTour}
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        <div className="flex flex-col text-zambezi gap-3 pl-5">
          <span>Stay in touch</span>
          <div className="flex gap-5 items-center  i">
            <Facebook className="fill-black" />
            <Instagram className="fill-black" />
            <Twitter className="fill-black" />
          </div>
        </div>
      </div>
    </SlideOver>
  );
}

export default memo(SlideMenuList);
